import type { ConversaDto } from './contratos/chamados'

/**
 * Em que bloco da lista do bate-papo cada conversa fica (ADR 0043).
 *
 * A ordem é a do pedido de 05/10/2026: primeiro o que chegou para mim e
 * ninguém respondeu (Solicitação de apoio), depois o que o suporte acabou de
 * devolver e pede revisão (Saiu do apoio), depois o cliente esperando (Não
 * respondidas), as minhas que estão com alguém de apoio (Com apoio), o
 * retorno agendado e, por último, o resto.
 *
 * Pura, sem tela: o teste confere a regra, e a lista só desenha os blocos.
 */

export const BLOCOS_DA_LISTA = [
  'solicitacao',
  'saiu-do-apoio',
  'nao-respondidas',
  'com-apoio',
  'retorno',
  'conversas',
] as const
export type BlocoDaLista = (typeof BLOCOS_DA_LISTA)[number]

export const ROTULO_DO_BLOCO: Record<BlocoDaLista, string> = {
  solicitacao: 'Solicitação de apoio',
  'saiu-do-apoio': 'Saiu do apoio',
  'nao-respondidas': 'Não respondidas',
  'com-apoio': 'Com apoio',
  retorno: 'Retorno agendado',
  conversas: 'Conversas',
}

export function blocoDaConversa(
  conversa: Pick<
    ConversaDto,
    'responsavelId' | 'apoios' | 'pedidoDeApoioParaMim' | 'saiuDoApoio' | 'estado'
  >,
  usuarioId: string,
  /** A marca de não respondida, como a lista a vê agora (ela segura a posição por um instante). */
  naoRespondida: boolean,
): BlocoDaLista {
  if (conversa.pedidoDeApoioParaMim) return 'solicitacao'
  const ehMinha = conversa.responsavelId === usuarioId
  if (ehMinha && conversa.saiuDoApoio) return 'saiu-do-apoio'
  // A vez é de quem está de apoio: para o dono, ela não fica verde nem toca.
  if (ehMinha && conversa.apoios.length > 0) return 'com-apoio'
  if (naoRespondida) return 'nao-respondidas'
  if (conversa.estado === 'retorno') return 'retorno'
  return 'conversas'
}

/** Quem está olhando é apoio aceito nesta conversa, e ela é de outra pessoa. */
export function souApoioNaConversa(
  conversa: Pick<ConversaDto, 'responsavelId' | 'apoios'>,
  usuarioId: string,
): boolean {
  return (
    conversa.responsavelId !== usuarioId && conversa.apoios.some((a) => a.usuarioId === usuarioId)
  )
}
